import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { ArrowRight, Globe, Loader2, Lock } from "lucide-react";

const FEATURES = [
  { emoji: "🔍", bg: "callout-blue", title: "SEO Audit", body: "Meta tags, headings, alt text, canonical, robots, sitemap, HTTPS, mobile, and content depth." },
  { emoji: "🤖", bg: "callout-purple", title: "GEO Audit", body: "Schema, FAQ, answerability, E-E-A-T — everything AI engines check before citing you." },
  { emoji: "🎯", bg: "callout-orange", title: "Keyword Strategy", body: "AI-suggested keywords with intent and difficulty, tailored to your content." },
  { emoji: "✅", bg: "callout-green", title: "Prioritized Plan", body: "P0/P1/P2 checklist with concrete steps you can hand to any dev or writer." },
  { emoji: "📈", bg: "callout-yellow", title: "Scan History", body: "Track improvements over time. Re-scan after fixes and watch the scores climb." },
  { emoji: "✨", bg: "callout-red", title: "AI Insights", body: "Executive summary and GEO tactics generated by GPT-5.2 for every scan." },
];

const STEPS = [
  { n: "1", emoji: "🌐", title: "Enter a URL", body: "Or paste raw content. We fetch the page, parse the DOM, and check robots.txt and sitemap.xml." },
  { n: "2", emoji: "📊", title: "Score & diagnose", body: "Two independent scores (SEO + GEO), 40+ heuristic checks, prioritized issue triage." },
  { n: "3", emoji: "🚀", title: "Get your plan", body: "GPT-5.2 turns findings into a step-by-step action plan with keywords and GEO tactics." },
];

export default function Landing() {
  const { user } = useAuth();
  const authed = user && user !== false;
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(null); // { used, limit, remaining }
  const [showWall, setShowWall] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authed) return;
    api.get("/scan/guest/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, [authed]);

  const onScan = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setError("");
    if (!authed && quota && quota.remaining <= 0) {
      setShowWall(true);
      trackEvent("guest_wall_shown", { reason: "quota_exhausted" });
      return;
    }
    setBusy(true);
    trackEvent("scan_started", { source: "landing", authed: authed });
    try {
      if (authed) {
        const { data } = await api.post("/scans", { url: url.trim() });
        trackEvent("scan_completed", { authed: true, seo_score: data.seo_score, geo_score: data.geo_score });
        navigate(`/scan/${data.id}`);
      } else {
        const { data } = await api.post("/scan/guest", { url: url.trim() });
        trackEvent("scan_completed", { authed: false, seo_score: data.seo_score, geo_score: data.geo_score });
        sessionStorage.setItem("guest_scan", JSON.stringify(data));
        setQuota({ used: data.guest_scans_used, limit: data.guest_scan_limit, remaining: Math.max(0, data.guest_scan_limit - data.guest_scans_used) });
        navigate("/preview");
      }
    } catch (err) {
      trackEvent("scan_failed", { authed: authed });
      if (err.response?.status === 402) {
        setShowWall(true);
        setQuota({ used: 3, limit: 3, remaining: 0 });
        trackEvent("guest_wall_shown", { reason: "server_402" });
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white text-[#37352F]">
      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 pt-20 md:pt-28 pb-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full callout-gray text-xs text-[#787774] mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#0F7B0F]"></span>
          <span>Live SEO + GEO analyzer · powered by GPT-5.2</span>
        </div>

        <h1 className="font-serif-h text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05] mb-6 text-[#37352F]">
          The workspace<br />for ranking <em className="text-[#2383E2] not-italic">everywhere</em>.
        </h1>
        <p className="max-w-2xl mx-auto text-lg text-[#787774] leading-relaxed mb-12">
          One scanner. Two engines. Audit your site for classic search <em>and</em> for how ChatGPT, Perplexity, and Gemini decide who to cite. Get a clean, step-by-step plan to fix everything.
        </p>

        <form onSubmit={onScan} className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-2" data-testid="hero-scan-form">
          <div className="flex-1 flex items-center border border-[#EBEBEA] rounded-lg bg-white focus-within:border-[#2383E2] focus-within:ring-2 focus-within:ring-[#D4EBF8] transition">
            <Globe className="w-4 h-4 text-[#9B9A97] ml-4" />
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter any website URL…"
              data-testid="hero-url-input"
              className="flex-1 bg-transparent px-3 py-3.5 text-base outline-none placeholder:text-[#9B9A97]"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="hero-scan-button"
            className="bg-[#2383E2] text-white font-medium px-6 py-3.5 rounded-lg flex items-center justify-center gap-2 hover:bg-[#1A6FCC] transition-colors disabled:opacity-60 disabled:cursor-wait min-w-[130px]"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Scanning…
              </>
            ) : (
              <>Analyze <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </form>

        {error && (
          <div className="max-w-2xl mx-auto callout-red rounded-md text-[#8B2A20] text-sm px-4 py-3 mt-4" data-testid="hero-scan-error">
            {error}
          </div>
        )}

        {!authed && quota && quota.remaining > 0 && (
          <p className="text-xs text-[#9B9A97] mt-4" data-testid="quota-remaining">
            <strong className="text-[#2383E2]">{quota.remaining} of {quota.limit}</strong> free scans remaining · No signup required
          </p>
        )}
        {authed && (
          <p className="text-xs text-[#9B9A97] mt-4">Signed in — unlimited scans</p>
        )}
        {!authed && quota && quota.remaining === 0 && (
          <p className="text-xs text-[#B0261D] mt-4" data-testid="quota-exhausted">
            You&apos;ve used all {quota.limit} free scans. <Link to="/signup" className="underline font-medium">Sign up for unlimited</Link>
          </p>
        )}
        {!authed && !quota && (
          <p className="text-xs text-[#9B9A97] mt-4">Free scan — no signup required · Results in ~20 seconds</p>
        )}
      </section>

      {/* Signup wall modal */}
      {showWall && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setShowWall(false)} data-testid="guest-wall">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-md w-full p-8 text-center border border-[#EBEBEA]">
            <span className="notion-emoji-box callout-yellow mx-auto mb-4" style={{ width: 56, height: 56, fontSize: 28 }}>🎁</span>
            <h2 className="font-serif-h text-2xl font-semibold mb-2">You&apos;ve used all 3 free scans</h2>
            <p className="text-[#787774] mb-6">Create a free account to keep scanning — unlimited scans, full history, and reports you can save.</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate("/signup")}
                data-testid="wall-signup-btn"
                className="bg-[#2383E2] text-white font-medium py-2.5 rounded-md flex items-center justify-center gap-2 hover:bg-[#1A6FCC]"
              >
                Sign up free <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/login")}
                data-testid="wall-login-btn"
                className="border border-[#EBEBEA] text-[#37352F] font-medium py-2.5 rounded-md hover:bg-[#F7F7F5]"
              >
                Log in
              </button>
              <button
                onClick={() => setShowWall(false)}
                className="text-[#9B9A97] text-xs mt-2 hover:text-[#37352F]"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
      {/* LOGOS STRIP */}
      <section className="border-y border-[#EBEBEA] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-widest text-[#9B9A97] mb-6">Optimizes for every search surface</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[#787774] font-medium">
            <span>Google</span>
            <span>ChatGPT</span>
            <span>Perplexity</span>
            <span>Gemini</span>
            <span>Claude</span>
            <span>Bing</span>
          </div>
        </div>
      </section>

      {/* WHAT IS GEO — callout */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="callout-blue rounded-lg p-8 md:p-10 flex gap-5">
          <span className="text-3xl">💡</span>
          <div>
            <h3 className="font-semibold text-lg mb-2 text-[#37352F]">What is GEO?</h3>
            <p className="text-[#37352F] leading-relaxed">
              <strong>Generative Engine Optimization</strong> is the practice of making your content <em>citable</em> by AI assistants. When someone asks ChatGPT or Perplexity a question, GEO decides whether your page shows up in the answer — with a link back to you. We score it, explain the gaps, and hand you the fix.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="mb-12 text-center">
          <h2 className="font-serif-h text-4xl md:text-5xl font-semibold tracking-tight mb-3">Everything in one page.</h2>
          <p className="text-[#787774] text-lg">A complete audit and a plan to fix it — nothing extra.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-[#EBEBEA] rounded-lg p-6 hover:bg-[#FAFAF9] transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <span className={`notion-emoji-box ${f.bg}`}>{f.emoji}</span>
                <h3 className="font-semibold text-lg text-[#37352F]">{f.title}</h3>
              </div>
              <p className="text-[#787774] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="mb-12 text-center">
          <h2 className="font-serif-h text-4xl md:text-5xl font-semibold tracking-tight mb-3">Three steps. Zero fluff.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s) => (
            <div key={s.n} className="border border-[#EBEBEA] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="notion-emoji-box callout-gray">{s.emoji}</span>
                <span className="text-sm text-[#9B9A97] font-medium">Step {s.n}</span>
              </div>
              <h3 className="font-semibold text-lg mb-2 text-[#37352F]">{s.title}</h3>
              <p className="text-[#787774] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif-h text-4xl md:text-5xl font-semibold tracking-tight mb-4">Ready to rank everywhere?</h2>
        <p className="text-[#787774] text-lg mb-8">Run your first scan in under a minute.</p>
        <Link
          to={authed ? "/dashboard" : "/signup"}
          data-testid="landing-cta-button"
          className="inline-flex items-center gap-2 bg-[#2383E2] text-white font-medium px-6 py-3.5 rounded-lg hover:bg-[#1A6FCC] transition-colors"
        >
          Get started &mdash; it&apos;s free <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="border-t border-[#EBEBEA]">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between gap-3 text-sm text-[#9B9A97]">
          <span>© {new Date().getFullYear()} Rank Notes — SEO + GEO analyzer</span>
          <span>Powered by GPT-5.2</span>
        </div>
      </footer>
    </div>
  );
}
